import { Box, Diamond, Eye, EyeOff, Network, Orbit } from 'lucide-react';
import { useSSA } from './store';
import type { CatalogueStyle } from './visuals';
import { CONNECTION_PALETTE, connectionCss, type ConnectionMode } from './connectionColors';
import { PROXIMITY_COLOURS } from './monitoring';
import { ProximityControls } from './ProximityControls';

export function CatalogueLabelsToggle() {
  const labels = useSSA((s) => s.labels),
    alertsOnly = useSSA((s) => s.alertsOnly);
  const Icon = labels ? Eye : EyeOff;
  return (
    <button
      type="button"
      aria-label="Show object labels"
      aria-pressed={labels}
      title="Cycle object labels: alerts only, off, all"
      onClick={() =>
        useSSA.setState(
          !labels
            ? { labels: true, alertsOnly: false }
            : alertsOnly
              ? { labels: false }
              : { alertsOnly: true },
        )
      }
      className={`pointer-events-auto flex items-center gap-2 rounded-full border px-3 py-2 text-xs ${labels ? 'border-sky-200/30 bg-[#0c1922]/95 text-sky-100' : 'border-slate-500/30 bg-[#0c1922]/95 text-slate-400'}`}
    >
      <Icon size={14} />
      Labels: {labels ? (alertsOnly ? 'alerts' : 'all') : 'off'}
    </button>
  );
}

export function ConnectionLegend() {
  const mode = useSSA((s) => s.connectionMode);
  const zones = useSSA((s) => s.zones);
  if (mode === 'proximity')
    return (
      <div aria-label="Connection colour legend" className="text-[10px] text-slate-400">
        <div className="flex items-center justify-between gap-2">
          {[
            ['Context', PROXIMITY_COLOURS[0]],
            ['≤' + zones.watch + ' km', PROXIMITY_COLOURS[1]],
            ['≤' + zones.close + ' km', PROXIMITY_COLOURS[2]],
          ].map(([label, color]) => (
            <span key={label} className="flex items-center gap-1">
              <span className="h-0.5 w-4" style={{ backgroundColor: color }} />
              {label}
            </span>
          ))}
        </div>
        <p className="mt-1">Arrows: closing / opening · pulse: closing within {zones.inner} km</p>
      </div>
    );
  if (mode === 'uniform')
    return <p className="text-[10px] text-slate-400">Uniform connection colour · geometry only</p>;
  const labels = mode === 'motion' ? ['Closing', 'Near steady', 'Opening'] : ['Behind', 'Alongside', 'Ahead'];
  return (
    <div className="text-[10px] text-slate-300" aria-label="Connection colour legend">
      <div className="flex items-center justify-between gap-2">
        {labels.map((label, i) => (
          <span key={label} className="flex items-center gap-1">
            <span
              className="h-0.5 w-4"
              style={{
                backgroundColor: connectionCss(
                  [CONNECTION_PALETTE.negative, CONNECTION_PALETTE.neutral, CONNECTION_PALETTE.positive][i],
                ),
              }}
            />
            {label}
          </span>
        ))}
      </div>
      <div
        className="mt-1 h-1 rounded-full opacity-80"
        style={{
          background: `linear-gradient(90deg,${connectionCss(CONNECTION_PALETTE.negative)},${connectionCss(CONNECTION_PALETTE.neutral)},${connectionCss(CONNECTION_PALETTE.positive)})`,
        }}
      />
      <p className="mt-1 text-slate-400">
        {mode === 'motion'
          ? 'Colour strength follows range rate. Not a risk level.'
          : 'Along-track position in the selected orbit frame. Peer mesh stays neutral.'}
      </p>
    </div>
  );
}
export function CatalogueDisplayControls() {
  const style = useSSA((s) => s.style),
    tumble = useSSA((s) => s.tumble),
    mesh = useSSA((s) => s.mesh),
    debris = useSSA((s) => s.debris),
    rockets = useSSA((s) => s.rockets),
    fragments = useSSA((s) => s.fragments);
  const connectionMode = useSSA((s) => s.connectionMode);
  const set = useSSA((s) => s.set);
  const choices = [
    ['dots', 'Dots', Orbit],
    ['solid', 'Solid models', Box],
    ['wire', 'Wireframe', Diamond],
  ] as const;
  return (
    <details className="relative pointer-events-auto text-xs">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-full border border-sky-200/20 bg-[#0c1922]/95 px-3 py-2 text-sky-100">
        <Diamond size={14} />
        {style === 'dots' ? 'Dots' : style === 'solid' ? 'Solid models' : 'Wireframe'}
      </summary>
      <div className="absolute left-0 top-full z-40 mt-2 max-h-[calc(100vh-160px)] w-72 overflow-y-auto rounded-xl border border-sky-200/25 bg-[#091720]/95 p-3 shadow-2xl backdrop-blur-md">
        <p className="mb-2 text-[10px] tracking-widest text-sky-300">CATALOGUE DISPLAY</p>
        <div className="space-y-1" role="group" aria-label="Catalogue display style">
          {choices.map(([value, label, Icon]) => (
            <button
              key={value}
              aria-pressed={style === value}
              className={`flex w-full items-center gap-2 rounded border px-3 py-2 text-left ${style === value ? 'border-sky-300/60 bg-sky-300/15 text-sky-100' : 'border-transparent text-slate-300 hover:bg-white/5'}`}
              onClick={() => set({ style: value as CatalogueStyle })}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
        <label className="mt-3 block text-slate-300">
          Connection colours
          <select
            aria-label="Connection colour mode"
            className="mt-1 w-full rounded border border-sky-200/25 bg-[#10202a] px-2 py-2 text-sky-100"
            value={connectionMode}
            onChange={(event) => set({ connectionMode: event.target.value as ConnectionMode })}
          >
            <option value="proximity">Proximity monitoring</option>
            <option value="motion">Closing / opening</option>
            <option value="along-track">Ahead / behind</option>
            <option value="uniform">Uniform</option>
          </select>
        </label>
        <p className="mt-1 text-[10px] leading-relaxed text-slate-400">
          {connectionMode === 'proximity'
            ? 'Colour shows current separation. Directional arrows show relative motion.'
            : connectionMode === 'motion'
              ? 'Colour follows closing / opening speed.'
              : connectionMode === 'along-track'
                ? 'Position along the selected orbit; not approach direction.'
                : 'One connection colour. Directional arrows remain available.'}
        </p>
        <ProximityControls />
        <label className="mt-3 flex items-center gap-2">
          <input type="checkbox" checked={mesh} onChange={(e) => set({ mesh: e.target.checked })} />
          <Network size={14} />
          Neighbourhood mesh
        </label>
        <p className="mt-1 text-[10px] leading-relaxed text-slate-400">
          Select an object to connect nearby neighbours within the search radius and clear of Earth. Predicted
          geometry, not measured communications.
        </p>
        <label className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={tumble}
            disabled={style === 'dots'}
            onChange={(e) => set({ tumble: e.target.checked })}
          />
          Illustrative tumbling
        </label>
        <p className="mt-1 text-[10px] leading-relaxed text-slate-400">
          Nearby rocket bodies and fragments only. Shapes and display sizes are illustrative; attitude and
          spin are not measured.
        </p>
        <div className="mt-3 border-t border-sky-200/15 pt-2">
          <p className="mb-2 text-[10px] text-slate-400">
            DEBRIS LAYERS {debris ? '' : '· switch Debris on to show'}
          </p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={rockets} onChange={(e) => set({ rockets: e.target.checked })} />
            Rocket bodies
          </label>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={fragments}
              onChange={(e) => set({ fragments: e.target.checked })}
            />
            Other debris / fragments
          </label>
        </div>
        <p className="mt-3 text-[10px] text-slate-400">
          Adaptive detail preserves the full point catalogue. Unknown objects retain a neutral diamond.
        </p>
      </div>
    </details>
  );
}
